import React from 'react';
import { Paper, Box, Typography, Divider, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { formatStudyDateTime } from '../../../utils/reportEditorUtils';
import StudyIndexBadge from './StudyIndexBadge';

function StudyInfoField({ label, value, sx = {} }) {
  return (
    <Box sx={{ px: 1.5, py: 0.5, flexShrink: 0, ...sx }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', wordBreak: 'break-word' }}>
        {value || '-'}
      </Typography>
    </Box>
  );
}

function StudyBlock({ exam, studyDate, t, studyIndex }) {
  const modality = exam?.modality || '-';
  const description = exam?.study_description || '-';
  const dateTime = formatStudyDateTime(exam, studyDate);
  const physician = exam?.referring_physician || '-';

  const title = `${modality} | ${description}`;

  return (
    <Box sx={{ py: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5, minWidth: 0 }}>
        {studyIndex != null && <StudyIndexBadge index={studyIndex} />}
        <Tooltip title={title} arrow placement="top">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {title}
          </Typography>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary">{dateTime}</Typography>
      <Typography variant="body2" color="text.secondary">{physician}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('report.department', 'Department')}: -
      </Typography>
    </Box>
  );
}

export default function StudyInfoCard({
  examData,
  combinedExams,
  isMultiExam,
  studyDate,
}) {
  const { t } = useTranslation();
  const isCombined = isMultiExam && combinedExams?.length > 1;

  const fields = [
    { label: t('report.modShort', 'Mod.'), value: examData?.modality },
    { label: t('report.studyDescription'), value: examData?.study_description },
    { label: t('report.dateTime', 'Date/Time'), value: formatStudyDateTime(examData, studyDate) },
    { label: t('report.refPhysicianShort', 'Ref. Physician'), value: examData?.referring_physician },
    { label: t('report.department', 'Department'), value: '-' },
  ];

  return (
    <Paper elevation={1} sx={{ flexShrink: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ py: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 2 }}>
          <InfoOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="h6" sx={{ color: 'primary.main' }}>
            {isCombined
              ? t('report.studyInfoCombined', 'Study Information — combined report')
              : t('report.studyInfo', 'Study Information')}
          </Typography>
        </Box>

        {isCombined ? (
          <Box sx={{ px: 2, overflowX: 'auto' }}>
            <Box
              sx={{
                display: 'inline-flex',
                width: 'fit-content',
                maxWidth: '100%',
                flexDirection: 'row',
                alignItems: 'stretch',
                flexWrap: 'nowrap',
              }}
            >
              {combinedExams.map((exam, index) => (
                <React.Fragment key={exam.id || exam.new_exam_id || index}>
                  {index > 0 && (
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider', mx: 0.5 }} />
                  )}
                  <Box sx={{ minWidth: 180, maxWidth: 280, flex: '1 1 0', px: index === 0 ? 0 : 1, overflow: 'hidden' }}>
                    <StudyBlock
                      exam={exam}
                      studyDate={exam.study_date || exam.observation_date}
                      t={t}
                      studyIndex={index + 1}
                    />
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ px: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                width: 'fit-content',
                maxWidth: '100%',
                flexDirection: 'row',
                alignItems: 'stretch',
                flexWrap: 'nowrap',
              }}
            >
              {fields.map((field, index) => (
                <React.Fragment key={field.label}>
                  {index > 0 && (
                    <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />
                  )}
                  <StudyInfoField label={field.label} value={field.value} sx={index === 0 ? { pl: 0 } : {}} />
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
